import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal      from "@react/shared/ui/Modal"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import Badge      from "@react/shared/ui/Badge"
import {
  createAcceptance, updateAcceptance,
  finalizeAcceptance, destroyAcceptance,
} from "../api/acceptance"
import { dateFmt } from "../helpers/format"

const DECISIONS = ["accepted", "accepted_with_reserves", "refused"]

const todayISO = () => new Date().toISOString().slice(0, 10)

const blankAttendee = () => ({ name: "", role: "" })

const AcceptanceModal = ({ isOpen, projectId, acceptanceData, liveReservesCount, canManage, onClose, onSaved }) => {
  const [decision,        setDecision]        = useState("accepted")
  const [acceptanceDate,  setAcceptanceDate]  = useState(todayISO())
  const [reservesDeadline, setReservesDeadline] = useState("")
  const [notes,           setNotes]           = useState("")
  const [attendees,       setAttendees]       = useState([blankAttendee()])
  const [saving,          setSaving]          = useState(false)
  const [errors,          setErrors]          = useState([])

  const existing  = acceptanceData?.acceptance
  const finalized = !!existing?.finalized

  useEffect(() => {
    if (!isOpen) return
    setErrors([])
    if (existing) {
      setDecision(existing.decision || "accepted")
      setAcceptanceDate(existing.acceptance_date || todayISO())
      setReservesDeadline(existing.reserves_deadline || "")
      setNotes(existing.notes || "")
      setAttendees(
        Array.isArray(existing.attendees) && existing.attendees.length > 0
          ? existing.attendees.map(a => ({ name: a.name || "", role: a.role || "" }))
          : [blankAttendee()]
      )
    } else {
      setDecision(liveReservesCount > 0 ? "accepted_with_reserves" : "accepted")
      setAcceptanceDate(todayISO())
      setReservesDeadline("")
      setNotes("")
      setAttendees([blankAttendee()])
    }
  }, [isOpen, existing, liveReservesCount])

  const updateAttendee = (idx, patch) => {
    setAttendees(prev => prev.map((a, i) => i === idx ? { ...a, ...patch } : a))
  }
  const addAttendee = () => setAttendees(prev => [...prev, blankAttendee()])
  const removeAttendee = (idx) => setAttendees(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)

  const buildBody = () => ({
    decision,
    acceptance_date:   acceptanceDate || null,
    reserves_deadline: (decision === "accepted_with_reserves" ? reservesDeadline : "") || null,
    notes,
    attendees: attendees.filter(a => a.name?.trim() || a.role?.trim()),
  })

  const onResponse = (response) => {
    if (response?.errors) {
      setErrors(Array.isArray(response.errors) ? response.errors : [String(response.errors)])
      return false
    }
    onSaved && onSaved(response)
    return true
  }

  const handleSave = () => {
    if (saving) return
    setSaving(true)
    setErrors([])

    const request = existing ? updateAcceptance(projectId, buildBody()) : createAcceptance(projectId, buildBody())
    request
      .then(onResponse)
      .catch(() => setErrors([I18n.t("projects.acceptances.errors.network")]))
      .finally(() => setSaving(false))
  }

  const handleFinalize = () => {
    if (!confirm(I18n.t("projects.acceptances.confirm_finalize"))) return
    if (saving) return
    setSaving(true)
    setErrors([])

    const proceed = existing
      ? updateAcceptance(projectId, buildBody())
      : createAcceptance(projectId, buildBody())

    proceed
      .then(r => {
        if (r?.errors) {
          setErrors(Array.isArray(r.errors) ? r.errors : [String(r.errors)])
          return null
        }
        return finalizeAcceptance(projectId)
      })
      .then(r => {
        if (!r) return
        const ok = onResponse(r)
        if (ok) onClose()
      })
      .catch(() => setErrors([I18n.t("projects.acceptances.errors.network")]))
      .finally(() => setSaving(false))
  }

  const handleDelete = () => {
    if (!confirm(I18n.t("projects.acceptances.confirm_destroy"))) return
    destroyAcceptance(projectId).then(r => {
      if (r?.success) {
        onSaved && onSaved(null)
        onClose()
      } else if (r?.errors) {
        setErrors(r.errors)
      }
    })
  }

  const footer = (
    <>
      <div className="flex items-center gap-2">
        {existing && canManage && !finalized && (
          <Button variant="ghost" onClick={handleDelete}>
            <FontAwesomeIcon icon="trash" /> {I18n.t("projects.acceptances.form.destroy")}
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>{I18n.t("projects.acceptances.form.close")}</Button>
        {canManage && !finalized && (
          <>
            <Button variant="secondary" onClick={handleSave} disabled={saving}>
              <FontAwesomeIcon icon={saving ? "spinner" : "floppy-disk"} spin={saving} />
              {I18n.t("projects.acceptances.form.save_draft")}
            </Button>
            <Button variant="primary" onClick={handleFinalize} disabled={saving}>
              <FontAwesomeIcon icon={saving ? "spinner" : "stamp"} spin={saving} />
              {I18n.t("projects.acceptances.form.finalize")}
            </Button>
          </>
        )}
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {finalized
        ? I18n.t("projects.acceptances.form.eyebrow_finalized")
        : (existing ? I18n.t("projects.acceptances.form.eyebrow_draft") : I18n.t("projects.acceptances.form.eyebrow_new"))}
      title   = {I18n.t("projects.acceptances.form.title")}
      footer  = {footer}
    >
      {finalized && (
        <div className="mb-4 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2">
          <FontAwesomeIcon icon="lock" />
          {I18n.t("projects.acceptances.form.finalized_hint", { date: dateFmt(existing.finalized_at) })}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.acceptances.form.decision")}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {DECISIONS.map(d => (
              <button
                key       = {d}
                type      = "button"
                disabled  = {finalized || !canManage}
                onClick   = {() => setDecision(d)}
                className = {`px-3 py-2 rounded-md border text-[12.5px] font-medium transition-colors ${
                  decision === d
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                }`}
              >
                {I18n.t(`projects.acceptances.decisions.${d}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-6">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.acceptances.form.acceptance_date")} <span className="text-rose-500">*</span>
          </label>
          <input
            type      = "date"
            value     = {acceptanceDate || ""}
            onChange  = {e => setAcceptanceDate(e.target.value)}
            disabled  = {finalized || !canManage}
            className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
          />
        </div>

        {decision === "accepted_with_reserves" && (
          <div className="col-span-6">
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {I18n.t("projects.acceptances.form.reserves_deadline")}
            </label>
            <input
              type      = "date"
              value     = {reservesDeadline || ""}
              onChange  = {e => setReservesDeadline(e.target.value)}
              disabled  = {finalized || !canManage}
              className = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm"
            />
          </div>
        )}

        <div className="col-span-12">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-slate-700">
              {I18n.t("projects.acceptances.form.attendees")}
            </label>
            {!finalized && canManage && (
              <button
                type      = "button"
                onClick   = {addAttendee}
                className = "text-[12px] text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
              >
                <FontAwesomeIcon icon="plus" /> {I18n.t("projects.acceptances.form.add_attendee")}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {attendees.map((a, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input
                  value     = {a.name}
                  onChange  = {e => updateAttendee(idx, { name: e.target.value })}
                  disabled  = {finalized || !canManage}
                  placeholder = {I18n.t("projects.acceptances.form.attendee_name")}
                  className = "col-span-5 h-9 px-3 rounded-md border border-slate-200 text-sm"
                />
                <input
                  value     = {a.role}
                  onChange  = {e => updateAttendee(idx, { role: e.target.value })}
                  disabled  = {finalized || !canManage}
                  placeholder = {I18n.t("projects.acceptances.form.attendee_role")}
                  className = "col-span-6 h-9 px-3 rounded-md border border-slate-200 text-sm"
                />
                <div className="col-span-1 flex justify-end">
                  {!finalized && canManage && attendees.length > 1 && (
                    <IconButton tone="danger" size="md" onClick={() => removeAttendee(idx)} title="">
                      <FontAwesomeIcon icon="xmark" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-12">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            {I18n.t("projects.acceptances.form.notes")}
          </label>
          <textarea
            value     = {notes}
            onChange  = {e => setNotes(e.target.value)}
            disabled  = {finalized || !canManage}
            rows      = "3"
            placeholder = {I18n.t("projects.acceptances.form.notes_placeholder")}
            className = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
          />
        </div>

        {!finalized && (
          <div className="col-span-12 text-[12px] text-slate-500">
            {I18n.t("projects.acceptances.form.live_reserves_hint", { count: liveReservesCount || 0 })}
          </div>
        )}

        {finalized && existing.reserves_snapshot && existing.reserves_snapshot.length > 0 && (
          <div className="col-span-12 border-t border-slate-200 pt-4">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
              {I18n.t("projects.acceptances.form.snapshot_title", { count: existing.reserves_snapshot.length })}
            </div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
              {existing.reserves_snapshot.map((r, i) => (
                <div key={i} className="px-3 py-2 text-[12px] flex items-center gap-3">
                  <span className="font-mono text-slate-500 w-12">{r.number}</span>
                  <span className="flex-1 text-slate-900 truncate">{r.title}</span>
                  <Badge tone="neutral" variant="soft" size="xs">
                    {I18n.t(`projects.reserves.severities.${r.severity}`)}
                  </Badge>
                  <Badge tone="neutral" variant="soft" size="xs">
                    {I18n.t(`projects.reserves.statuses.${r.status}`)}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="mt-4 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}
    </Modal>
  )
}

export default AcceptanceModal
